import {
  FormControl,
  InputLabel,
  MenuItem,
  TextField as TextFieldMaterial
} from '@material-ui/core';
import { Button, FormikAutocompleteAsync } from '@vadiun/react-components';
import { useVerifyAction } from '@vadiun/react-hooks-legacy';
import { ClientPrivateModel } from 'app/client/private/private/models/ClientPrivate';
import {
  CANALES_DE_VIAJE,
  TIPOS_SERVICIO_VIAJE_TRASLADO,
  ViajeTrasladoTipoServicio
} from 'app/viaje/models';
import {
  ErrorMessage,
  Field,
  FieldArray,
  Form,
  Formik,
  FormikProps
} from 'formik';
import { CheckboxWithLabel, Select, TextField } from 'formik-material-ui';
import { AutocompleteRenderInputParams } from 'formik-material-ui-lab';
import { KeyboardDateTimePicker } from 'formik-material-ui-pickers';
import { useMemo, useRef } from 'react';
import { StaticMap } from 'shared/components';
import FormikOnFieldChange from 'shared/components/FormikOnFieldChange';
import { TransferReservationLocationFormModal } from '../../../viajeTraslado/components/TransferReservationLocationFormModal';
import { transferReservationPrivateSingleFormValidation } from './TransferReservationPrivateSingleFormValidation';
import { TransferReservationPrivateSingleFormType } from './TransferReservationSingleFormType';
import { createTransferReservationPrivateSingleInitialValues } from './TransferreservationPrivateSingleFormInitialValues';
import moment from 'moment';

interface Props {
  initialValues?: TransferReservationPrivateSingleFormType;
  onSubmit: (
    viajeFormValues: TransferReservationPrivateSingleFormType,
    reset
  ) => void;
  getClientesByName: (name: string) => Promise<ClientPrivateModel[]>;
  onCalculateDistanceAndCost: (
    viajeFormValues: TransferReservationPrivateSingleFormType
  ) => void;
}

export function TrasnferReservationPrivateSingleForm({
  initialValues: _initialValues,
  onSubmit,
  getClientesByName,
  onCalculateDistanceAndCost
}: Props) {
  const initialValues = useMemo(
    () =>
      _initialValues ?? createTransferReservationPrivateSingleInitialValues(),
    [_initialValues]
  );
  const formRef =
    useRef<FormikProps<TransferReservationPrivateSingleFormType>>(null);
  const verifyAction = useVerifyAction();
  const isPast = (viaje) =>
    moment(viaje.fechaYHoraDeLlegada).isBefore(
      moment().subtract(15, 'minutes')
    );

  return (
    <Formik<TransferReservationPrivateSingleFormType>
      innerRef={formRef}
      initialValues={initialValues}
      validationSchema={transferReservationPrivateSingleFormValidation}
      enableReinitialize
      onSubmit={onSubmit}
    >
      {({ touched, errors, values, submitForm, setFieldValue }) => (
        <Form className="w-full">
          <FormikOnFieldChange<TransferReservationPrivateSingleFormType>
            field="cliente"
            onChange={(
              cliente: TransferReservationPrivateSingleFormType['cliente']
            ) => {
              if (cliente === null) {
                return;
              }
              if (values.puntos.length >= 2) {
                return;
              }
              const puntos = [
                {
                  domicilio: cliente.address,
                  espera: '',
                  observacion: '',
                  pasajeros: []
                }
              ];
              setFieldValue('puntos', puntos);
            }}
          />
          <Field
            name="cliente"
            component={FormikAutocompleteAsync}
            fetchOptions={getClientesByName}
            fullWidth
            getOptionLabel={(cliente: ClientPrivateModel) =>
              cliente.name + ' ' + cliente.lastName + ' (' + cliente.code + ')'
            }
            className="w-1/2"
            renderInput={(params: AutocompleteRenderInputParams) => (
              <TextFieldMaterial
                {...params}
                error={touched['cliente'] && !!errors['cliente']}
                helperText={errors['cliente']}
                label="Cliente"
                variant="outlined"
                fullWidth
              />
            )}
          />
          <div className="grid grid-cols-12 gap-6 border-b-2 border-dashed py-8">
            <FormControl variant="outlined" fullWidth className="col-span-3">
              <InputLabel>Tipo de servicio</InputLabel>
              <Field
                component={Select}
                name="tipoServicio"
                label="Tipo de servicio"
              >
                {TIPOS_SERVICIO_VIAJE_TRASLADO.map((canal) => (
                  <MenuItem value={canal} key={canal}>
                    {canal}
                  </MenuItem>
                ))}
              </Field>
            </FormControl>
            <FormControl variant="outlined" fullWidth className="col-span-3">
              <InputLabel>Canal de ingreso</InputLabel>
              <Field
                component={Select}
                name="canalIngreso"
                label="Canal de ingreso"
              >
                {CANALES_DE_VIAJE.map((canal) => (
                  <MenuItem value={canal} key={canal}>
                    {canal}
                  </MenuItem>
                ))}
              </Field>
            </FormControl>
            <div className="col-span-2">
              <Field
                component={CheckboxWithLabel}
                type="checkbox"
                name="prioridad"
                color="primary"
                Label={{ label: 'Prioritario' }}
                fullWidth
              />
            </div>
            <div className="col-span-2">
              <Field
                component={CheckboxWithLabel}
                type="checkbox"
                name="hasOutSite"
                color="primary"
                Label={{ label: 'Out Site' }}
              />
            </div>
          </div>
          <div className="border-b-2 border-dashed py-8 ">
            <Field
              name="observacionCoordinacion"
              label="Observacion coordinacion"
              variant="outlined"
              component={TextField}
              fullWidth
            />
          </div>
          <div className="border-b-2 border-dashed py-8">
            <Field
              name="generalObservation"
              label="Observacion Chofer"
              variant="outlined"
              component={TextField}
              fullWidth
            />
          </div>
          <div className="mt-8 flex space-x-8">
            <div className="flex-grow">
              <div className="flex space-x-8">
                <Field
                  component={KeyboardDateTimePicker}
                  label="Fecha de llegada"
                  name={`fechaYHoraDeLlegada`}
                  ampm={false}
                  inputVariant="outlined"
                  margin="normal"
                  className="flex-1"
                  format="DD/MM/YYYY HH:mm"
                />

                <Field
                  component={KeyboardDateTimePicker}
                  label="Tiempo previo salida"
                  name={`minutosPreviosASalida`}
                  ampm={false}
                  inputVariant="outlined"
                  margin="normal"
                  className="flex-1"
                  format="DD/MM/YYYY HH:mm"
                />
              </div>

              <FieldArray
                name={`puntos`}
                render={() => (
                  <TransferReservationLocationFormModal
                    pasajeros={[]}
                    puntos={values.puntos}
                    onEditPuntos={(puntos) => setFieldValue(`puntos`, puntos)}
                  />
                )}
              />
              <ErrorMessage name={`puntos`}>
                {(msg) => (
                  <p className="mt-4 text-sm text-red-500">
                    {JSON.stringify(msg)}
                  </p>
                )}
              </ErrorMessage>
            </div>
            <div
              className={`w-1/4 transition-all delay-300 duration-300 ease-in-out hover:w-2/5 ${
                values.puntos.length === 0 ? 'opacity-0' : 'opactiy-1'
              }`}
            >
              <StaticMap
                markers={values.puntos.map((punto) => ({
                  lat: punto.domicilio.latitud,
                  lng: punto.domicilio.longitud
                }))}
                size="600x600"
              />
            </div>
          </div>
          {isPast(values) && (
            <div className="mt-4 bg-red-100 border border-red-500 p-4 rounded">
              <p className="text-red-700 text-sm mb-2">
                ⚠️ Este viaje está siendo editado con una fecha anterior a la
                actual. Ingresá el motivo para continuar:
              </p>

              <Field
                name="reason_reserve_past"
                as="input"
                placeholder="Ejemplo: el cliente avisó tarde"
                className={`w-full p-2 border rounded text-sm ${
                  touched.reason_reserve_past && errors.reason_reserve_past
                    ? 'border-red-500'
                    : 'border-gray-300'
                }`}
              />

              <ErrorMessage name="reason_reserve_past">
                {(msg) => <p className="text-sm text-red-600 mt-1">{msg}</p>}
              </ErrorMessage>
            </div>
          )}

          <div className="mt-8 flex justify-between">
            <Button
              variant="contained"
              color="primary"
              disabled={values.puntos.length < 2 || values.tipoServicio === ViajeTrasladoTipoServicio.SERVICIOS_GRUPALES}
              onClick={() => onCalculateDistanceAndCost(values)}
            >
              Calcular costos y distancias
            </Button>

            <Button
              variant="contained"
              color="green"
              className="ml-auto"
              onClick={submitForm}
            >
              Guardar Viaje
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  );
}
